import { useMemo } from 'react';
import Text from 'common/ui/Text';
import { Stack } from 'common/ui/Layout';
import { css } from 'styled-components';
import { Colors, Spacings } from 'styles';
import { StudyCriteriaCategory } from 'generated/graphql';
import type { ExtendedStudyCriteria } from './HangingProtocolsFormV4';
import { NOT_SPECIFIED_TEXT } from './SeriesClassificationDisplay';
import { rootTheme } from 'theme/rootTheme';
import { BORDER_COLOR, HPDivider, TABLE_BG_COLOR } from './hpStyles';

export const DISPLAY_FIELDS = [
  { key: StudyCriteriaCategory.Modality, label: 'Modality' },
  { key: StudyCriteriaCategory.AnatomicFocus, label: 'Anatomic Focus' },
  { key: StudyCriteriaCategory.ContrastStatus, label: 'Contrast Status' },
  { key: StudyCriteriaCategory.ViewCount, label: 'View Count' },
  { key: StudyCriteriaCategory.ReasonForStudy, label: 'Reason for Study' },
  // { key: StudyCriteriaCategory.FieldOfView, label: 'Field of View' } # Not a requirement, but data is available as part of the study classification
];

export interface StudyClassificationDisplayProps {
  studyClassification: Array<ExtendedStudyCriteria>;
}

export const StudyClassificationDisplay = ({
  studyClassification,
}: StudyClassificationDisplayProps) => {
  const classificationData = useMemo(
    () =>
      DISPLAY_FIELDS.map((field) => {
        const classifications = studyClassification.filter(
          (classification) => classification.category === field.key
        );
        let value = classifications.map((classification) => classification.displayName).join('\n');
        value = value === '' ? NOT_SPECIFIED_TEXT : value;

        return {
          key: field.key,
          label: field.label,
          value,
        };
      }),
    [studyClassification]
  );

  return (
    <div
      css={css`
        background-color: ${TABLE_BG_COLOR};
        border-radius: ${rootTheme.radius.xs}px;
        border: 1px solid ${BORDER_COLOR};
        span {
          font-size: 14px;
        }
      `}
    >
      {classificationData.map((field, index) => (
        <div key={field.key}>
          <Stack
            alignY="center"
            css={css`
              padding: ${Spacings.xsmall}rem 16px;
              gap: 16px;
            `}
          >
            <Text
              css={css`
                flex: 1;
                color: ${Colors.lightSubtler};
                line-height: 24px;
              `}
            >
              {field.label}
            </Text>
            <Text
              css={css`
                text-align: right;
                white-space: pre-line;
                ${field.value === NOT_SPECIFIED_TEXT
                  ? `color: ${Colors.lightSubtler};`
                  : `color: ${Colors.light};`}
              `}
              data-testid={`protocol-field-${field.key}`}
            >
              {field.value}
            </Text>
          </Stack>
          {index < classificationData.length - 1 && <HPDivider />}
        </div>
      ))}
    </div>
  );
};
